import { useRef, useState } from 'react';

import { t } from '@ping/helpers';

import {
  MAX_REVIEW_DOCUMENT_BYTES,
  submitKybReview,
  uploadFailureMessage,
  uploadKybReviewDocument,
} from './kyb-review-documents.api';

export interface IReviewDocumentsOptions {
  onSubmitted: (submittedNames: string[]) => void;
  uploadDocument?: (file: File) => Promise<unknown>;
  submitReview?: () => Promise<unknown>;
}

export const useReviewDocuments = ({
  onSubmitted,
  uploadDocument = uploadKybReviewDocument,
  submitReview = submitKybReview,
}: IReviewDocumentsOptions) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadedNames, setUploadedNames] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const handleFiles = async (list: FileList | null) => {
    const file = list?.[0];
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    if (!file) {
      return;
    }

    if (file.size > MAX_REVIEW_DOCUMENT_BYTES) {
      setError(t('This file is larger than the allowed size.'));
      return;
    }

    setError(undefined);
    setBusy(true);
    try {
      await uploadDocument(file);
      setUploadedNames(current => [...current, file.name]);
    } catch (uploadError) {
      setError(t(uploadFailureMessage(uploadError)));
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    setError(undefined);
    setBusy(true);
    setSubmitting(true);
    try {
      await submitReview();
      const submittedNames = uploadedNames;
      setUploadedNames([]);
      onSubmitted(submittedNames);
    } catch {
      setError(t('The documents could not be submitted for review.'));
    } finally {
      setBusy(false);
      setSubmitting(false);
    }
  };

  return {
    inputRef,
    uploadedNames,
    busy,
    submitting,
    error,
    canSubmit: !busy && uploadedNames.length > 0,
    openPicker: () => inputRef.current?.click(),
    handleFiles,
    handleSubmit,
  };
};

export const REVIEW_DOCUMENT_ACCEPT = 'image/jpeg,image/png,image/gif,application/pdf';
